/** API Keys management — real backend (Wave 5.7). Generate, list, revoke. */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import BlockIcon from "@mui/icons-material/Block";
import apiClient from "../api/client";

interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  account_id: string;
  scopes: string[];
  created_at: string | null;
  last_used_at: string | null;
  revoked: boolean;
}

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [scopesText, setScopesText] = useState("balance, cdrs, rates, invoices");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/api-keys");
      setKeys(response.data.keys || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const createKey = async () => {
    setCreating(true);
    setError("");
    try {
      const scopeList = scopesText.split(",").map((s) => s.trim()).filter(Boolean);
      const response = await apiClient.post(
        "/api/v1/api-keys",
        { name, account_id: accountId, scopes: scopeList },
        { headers: { "Content-Type": "application/json" } }
      );
      setNewKey(response.data.api_key);
      setCreateOpen(false);
      setName("");
      setAccountId("");
      await fetchKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (row: ApiKeyRow) => {
    setError("");
    try {
      await apiClient.post(`/api/v1/api-keys/${row.id}/revoke`);
      await fetchKeys();
      setNotice(`Key "${row.name}" (${row.key_prefix}) revoked.`);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to revoke key");
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keys authenticate external/customer access to the client API
            (<code>/api/v1/client/*</code>). Keys are stored hashed — the full
            key is shown exactly once at creation.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchKeys} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setNewKey(null); setCreateOpen(true); }}>
            Create API key
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {newKey && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            API key created — copy it now, it will not be shown again:
          </Typography>
          <Box
            component="code"
            sx={{ fontFamily: "monospace", bgcolor: "action.selected", p: 1, borderRadius: 1, display: "inline-block", wordBreak: "break-all" }}
          >
            {newKey}
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((k) => (
              <TableRow key={k.id} hover>
                <TableCell>{k.name}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {k.key_prefix}…
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {k.account_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  {k.scopes.map((s) => <Chip key={s} label={s} size="small" sx={{ mr: 0.5 }} />)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={k.revoked ? "Revoked" : "Active"}
                    color={k.revoked ? "default" : "success"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}
                </TableCell>
                <TableCell align="right">
                  {!k.revoked && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => revokeKey(k)}
                    >
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No API keys yet. Create one to give an account programmatic
                    access to balance, CDRs, rates and invoices endpoints.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="normal" label="Key name"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. prod integrations"
          />
          <TextField
            fullWidth margin="normal" label="Billing engine account ID"
            value={accountId} onChange={(e) => setAccountId(e.target.value)}
            placeholder="e.g. the account UUID"
            helperText="The key is scoped to this account and its downstream subtree"
          />
          <TextField
            fullWidth margin="normal" label="Scopes (comma-separated)"
            value={scopesText} onChange={(e) => setScopesText(e.target.value)}
            helperText="Allowed: balance, cdrs, rates, invoices"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={createKey}
            disabled={creating || !name || !accountId}
          >
            {creating ? "Creating…" : "Create key"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
